import React, { useMemo } from 'react';
import { useData } from '../DataContext';
import { useLang } from '../LanguageContext'; 
import { useNavigate } from 'react-router-dom';
import { Gamepad2, TrendingUp, Coins, Banknote, Wallet } from 'lucide-react';

const Dashboard = () => {
  // permissions 데이터 가져오기
  const { games, agents, transactions, agentTransactions, funds, currentUser, permissions } = useData();
  const { t } = useLang(); 
  const navigate = useNavigate();

  const fmt = (num) => num ? Math.floor(Number(num)).toLocaleString() : '0';
  const parseNum = (val) => parseFloat(String(val).replace(/,/g, '')) || 0;

  // ★ [기존 유지] 관리자 권한 목록에 'Senior Manager' 명시적 추가
  const isAdmin = currentUser && ['Master_Admin', 'Senior Manager', 'Manager', 'Staff'].includes(currentUser.type);
  
  // ★ [기존 유지] Cash Flow 카드 보기 권한 체크
  const canViewCash = useMemo(() => {
      if (!currentUser) return false;
      // Master_Admin과 Senior Manager는 무조건 볼 수 있음 (기존 로직 유지)
      if (['Master_Admin', 'Senior Manager'].includes(currentUser.type)) return true;
      
      // 그 외는 Settings 설정을 따름
      return permissions?.ShowCash?.[currentUser.type] === true;
  }, [currentUser, permissions]);

  // 내 에이전트 정보 (관리자가 아닌 경우 본인 잔고 표시용)
  const myAgentProfile = agents.find(a => a.code === currentUser?.code);

  // --- 통계 계산 로직 (기존 100% 유지) ---
  const stats = useMemo(() => {
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const monthStr = now.toISOString().slice(0, 7);
    const yearStr = now.toISOString().slice(0, 4);

    // 1. Rolling Stats
    let rollingToday = 0, rollingMonth = 0, rollingYear = 0;
    
    Object.values(transactions).flat().forEach(tx => {
        const date = tx.date.slice(0, 10);
        const amt = parseNum(tx.amount);
        
        if (tx.type === 'Rolling' || tx.type === 'Initial Buy-in' || tx.type === 'Add Buy-in') {
            if (date === todayStr) rollingToday += amt;
            if (date.startsWith(monthStr)) rollingMonth += amt;
            if (date.startsWith(yearStr)) rollingYear += amt;
        }
        else if (['Chips Out', 'Cash-out', 'Settle'].includes(tx.type)) {
            const rollingReturn = parseNum(tx.rollingChips);
            if (date === todayStr) rollingToday -= rollingReturn;
            if (date.startsWith(monthStr)) rollingMonth -= rollingReturn;
            if (date.startsWith(yearStr)) rollingYear -= rollingReturn;
        }
    });

    // 2. Chips Inventory
    const initialCashChips = parseNum(funds?.cashChips || 0);
    const initialRollingChips = parseNum(funds?.rollingChips || 0);
    let currentCashChips = initialCashChips;
    let currentRollingChips = initialRollingChips;

    if (isAdmin) {
        Object.values(transactions).flat().forEach(tx => {
            const amt = parseNum(tx.amount);
            const rReturn = parseNum(tx.rollingChips);
            const cReturn = parseNum(tx.cashChips);

            if (['Initial Buy-in', 'Add Buy-in'].includes(tx.type)) {
                currentRollingChips -= amt; 
            }
            if (['Chips Out', 'Cash-out', 'Settle', 'Return'].includes(tx.type)) {
                currentRollingChips += rReturn; 
                currentCashChips += cReturn;    
            }
        });
    }

    const totalChipsBalance = currentCashChips + currentRollingChips;

    // 3. Cash Stats
    let cashIn = { day: 0, month: 0, year: 0 };
    let cashOut = { day: 0, month: 0, year: 0 };
    
    agentTransactions.forEach(tx => {
        const date = tx.date.slice(0, 10);
        const amt = parseNum(tx.amount);
        
        if (tx.type === 'Deposit') { 
            if (date === todayStr) cashIn.day += amt;
            if (date.startsWith(monthStr)) cashIn.month += amt;
            if (date.startsWith(yearStr)) cashIn.year += amt;
        } else if (tx.type === 'Withdraw') { 
            if (date === todayStr) cashOut.day += amt;
            if (date.startsWith(monthStr)) cashOut.month += amt;
            if (date.startsWith(yearStr)) cashOut.year += amt;
        }
    });

    const winLoss = {
        day: cashIn.day - cashOut.day,
        month: cashIn.month - cashOut.month,
        year: cashIn.year - cashOut.year
    };

    return {
        rolling: { day: rollingToday, month: rollingMonth, year: rollingYear },
        chips: { 
            cash: currentCashChips, 
            rolling: currentRollingChips, 
            total: totalChipsBalance,
            initCash: initialCashChips,      
            initRolling: initialRollingChips 
        },
        cash: { in: cashIn, out: cashOut, winLoss: winLoss }
    };
  }, [games, agents, transactions, agentTransactions, funds, isAdmin]); 

  // --- Active Games 필터링 ---
  const activeGames = useMemo(() => {
    return games.filter(g => g.status === 'Active').sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt));
  }, [games]);

  const handleEnterGame = (gameId) => {
      navigate('/games', { state: { targetGameId: gameId } });
  };

  const CardLabel = ({ children }) => <div style={{ fontSize:'13px', color:'#64748b', marginBottom:'5px' }}>{children}</div>;
  const CardValue = ({ children, color = '#0f172a' }) => <div style={{ fontSize:'20px', fontWeight:'bold', textAlign:'right', color }}>{children}</div>;
  const InitValue = ({ children }) => <div style={{ fontSize:'11px', color:'#94a3b8', textAlign:'right', marginTop:'2px' }}>Initial: {children}</div>;

  return (
    <div style={{ padding: '20px', color: '#1e293b', backgroundColor: '#f1f5f9', minHeight: '100vh' }}>
      
      <div style={{ marginBottom: '30px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, color: '#0f172a' }}>{isAdmin ? t('dash_title_admin') : t('dash_title_user')}</h2>
          <p style={{ color: '#64748b', marginTop: '5px' }}>{isAdmin ? t('dash_sub_admin') : t('dash_sub_user')}</p>
      </div>

      {/* ★ [모바일 호환 수정] repeat(3, 1fr) -> repeat(auto-fit, minmax(300px, 1fr)) */}
      {/* 화면이 좁아지면 카드가 자동으로 아래로 내려갑니다. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '40px' }}>
        
        {/* 1. Rolling Section */}
        <div style={{ display:'flex', flexDirection:'column' }}>
            <div style={{ backgroundColor:'#3b82f6', color:'white', padding:'12px 20px', borderRadius:'12px 12px 0 0', fontWeight:'bold', fontSize:'16px', display:'flex', alignItems:'center', gap:'8px' }}>
                <TrendingUp size={20}/> {isAdmin ? t('card_total_rolling') : t('card_my_rolling')}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'1px', backgroundColor:'#e2e8f0', border:'1px solid #e2e8f0', borderRadius:'0 0 12px 12px', overflow:'hidden' }}>
                <div style={{ background:'white', padding:'20px' }}>
                    <CardLabel>Today Total Rolling</CardLabel>
                    <CardValue>{fmt(stats.rolling.day)}</CardValue>
                </div>
                <div style={{ background:'white', padding:'20px' }}>
                    <CardLabel>Monthly Cumulative Rolling</CardLabel>
                    <CardValue>{fmt(stats.rolling.month)}</CardValue>
                </div>
                <div style={{ background:'white', padding:'20px' }}>
                    <CardLabel>Yearly Cumulative Rolling</CardLabel>
                    <CardValue>{fmt(stats.rolling.year)}</CardValue>
                </div>
            </div>
        </div>

        {/* 2. Chips Section */}
        <div style={{ display:'flex', flexDirection:'column' }}>
            <div style={{ backgroundColor:'#65a30d', color:'white', padding:'12px 20px', borderRadius:'12px 12px 0 0', fontWeight:'bold', fontSize:'16px', display:'flex', alignItems:'center', gap:'8px' }}>
                {isAdmin ? <><Coins size={20}/> {t('card_cage_inv')}</> : <><Wallet size={20}/> {t('card_wallet')}</>}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'1px', backgroundColor:'#e2e8f0', border:'1px solid #e2e8f0', borderRadius:'0 0 12px 12px', overflow:'hidden', height:'100%' }}>
                {isAdmin ? (
                    <>
                        <div style={{ background:'white', padding:'20px', flex:1 }}>
                            <CardLabel>{t('cash_chips_safe')}</CardLabel>
                            <CardValue>{fmt(stats.chips.cash)}</CardValue>
                            <InitValue>{fmt(stats.chips.initCash)}</InitValue>
                        </div>
                        <div style={{ background:'white', padding:'20px', flex:1 }}>
                            <CardLabel>{t('rolling_chips_safe')}</CardLabel>
                            <CardValue>{fmt(stats.chips.rolling)}</CardValue>
                            <InitValue>{fmt(stats.chips.initRolling)}</InitValue>
                        </div>
                        <div style={{ background:'white', padding:'20px', flex:1 }}>
                            <CardLabel>{t('total_chips_bal')}</CardLabel>
                            <CardValue>{fmt(stats.chips.total)}</CardValue>
                        </div>
                    </>
                ) : (
                    <div style={{ background:'white', padding:'40px 20px', flex:1, display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center' }}>
                        <CardLabel>{t('agent_balance')}</CardLabel>
                        <div style={{ fontSize:'36px', fontWeight:'900', color:'#65a30d', marginTop:'10px' }}>
                            {fmt(myAgentProfile?.balance || 0)}
                        </div>
                        <div style={{ fontSize:'13px', color:'#94a3b8', marginTop:'10px' }}>ID: {currentUser?.code}</div>
                    </div>
                )}
            </div>
        </div>

        {/* 3. Cash Section */}
        {canViewCash ? (
            <div style={{ display:'flex', flexDirection:'column' }}>
                <div style={{ backgroundColor:'#f97316', color:'white', padding:'12px 20px', borderRadius:'12px 12px 0 0', fontWeight:'bold', fontSize:'16px', display:'flex', alignItems:'center', gap:'8px' }}>
                    <Banknote size={20}/> {isAdmin ? 'Cash Flow' : t('card_cashflow')}
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:'1px', backgroundColor:'#e2e8f0', border:'1px solid #e2e8f0', borderRadius:'0 0 12px 12px', overflow:'hidden' }}>
                    {[t('today'), t('this_month'), t('this_year')].map((label, idx) => {
                        const key = idx === 0 ? 'day' : idx === 1 ? 'month' : 'year';
                        return (
                            <div key={label} style={{ background:'white', padding:'15px 20px' }}>
                                <div style={{ fontSize:'14px', fontWeight:'bold', color:'#f97316', marginBottom:'10px' }}>{label}</div>
                                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px' }}>
                                    <div>
                                        <div style={{fontSize:'11px', color:'#64748b'}}>{t('in_deposit')}</div>
                                        <div style={{fontWeight:'bold'}}>{fmt(stats.cash.in[key])}</div>
                                    </div>
                                    <div>
                                        <div style={{fontSize:'11px', color:'#64748b'}}>{t('out_withdraw')}</div>
                                        <div style={{fontWeight:'bold'}}>{fmt(stats.cash.out[key])}</div>
                                    </div>
                                    <div>
                                        <div style={{fontSize:'11px', color:'#64748b'}}>{t('net_flow')}</div>
                                        <div style={{fontWeight:'bold', color: stats.cash.winLoss[key] >= 0 ? '#16a34a' : '#dc2626'}}>
                                            {fmt(stats.cash.winLoss[key])}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        ) : (
            // 권한 없을 때 대체 화면
            <div style={{ display:'flex', flexDirection:'column', height:'100%', opacity:0.5, pointerEvents:'none' }}>
                 <div style={{ backgroundColor:'#94a3b8', color:'white', padding:'12px 20px', borderRadius:'12px 12px 0 0', fontWeight:'bold', fontSize:'16px', display:'flex', alignItems:'center', gap:'8px' }}>
                    <Banknote size={20}/> Cash Flow (Hidden)
                </div>
                <div style={{ flex:1, background:'#f1f5f9', borderRadius:'0 0 12px 12px', border:'1px solid #cbd5e1', display:'flex', alignItems:'center', justifyContent:'center', color:'#94a3b8' }}>
                    Access Restricted
                </div>
            </div>
        )}
      </div>

      <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', color: '#0f172a', display:'flex', alignItems:'center', gap:'10px' }}>
        <Gamepad2 /> {t('active_tables')} ({activeGames.length})
      </h3>

      {/* ★ [모바일 호환 수정] 게임 리스트 카드 크기 조정 (minmax 350px -> 300px) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
        {activeGames.map(game => {
            const agent = agents.find(a => a.code === game.agentId);
            return (
                <div key={game.id} style={{ backgroundColor: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', border: '1px solid #e2e8f0' }}>
                    <div style={{ backgroundColor: '#84cc16', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>Game ID: {game.id}</div>
                        <div style={{ fontWeight: 'bold', fontSize: '14px', opacity: 0.9 }}>
                            {agent ? `${agent.code} ${agent.engSurname}` : game.agentId}
                        </div>
                    </div>
                    
                    <div style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '15px' }}>
                            <span style={{ color: '#334155' }}>{t('totalBuyIn')}</span>
                            <span style={{ fontWeight: 'bold', color: '#0f172a' }}>{fmt(game.sumBuyIn)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '15px' }}>
                            <span style={{ color: '#334155' }}>{t('totalCashOut')}</span>
                            <span style={{ fontWeight: 'bold', color: '#dc2626' }}>{fmt(game.sumCashOut)}</span>
                        </div>
                        <div style={{ borderTop: '1px solid #f1f5f9', margin: '15px 0' }}></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', alignItems:'center' }}>
                            <span style={{ color: '#334155', fontWeight:'bold' }}>{t('totalRolling')}</span>
                            <span style={{ fontWeight: 'bold', color: '#d97706', fontSize:'22px' }}>{fmt(game.totalRolling)}</span>
                        </div>
                    </div>

                    <div 
                        onClick={() => handleEnterGame(game.id)}
                        style={{ 
                            backgroundColor: '#84cc16', 
                            padding: '15px', 
                            textAlign: 'center', 
                            color: 'white', 
                            fontWeight: 'bold', 
                            fontSize: '16px', 
                            cursor: 'pointer', 
                            transition: 'background 0.2s',
                            display:'flex', justifyContent:'center', alignItems:'center', gap:'8px',
                            letterSpacing: '1px'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#65a30d'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#84cc16'}
                    >
                        {t('game_enter')}
                    </div>
                </div>
            );
        })}
        {activeGames.length === 0 && (
            <div style={{ gridColumn: '1 / -1', padding: '60px', textAlign: 'center', color: '#94a3b8', background: 'white', borderRadius: '12px', border:'1px dashed #cbd5e1' }}>
                <div style={{ fontSize:'18px', fontWeight:'bold' }}>{t('no_active_tables')}</div>
                <div style={{ marginTop:'10px', fontSize:'14px' }}>{t('start_new_game_msg')}</div>
            </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;